"use client";

import { useState } from "react";
import type { Route } from "next";
import Link from "next/link";
import { Search } from "lucide-react";
import { useTranslations } from "next-intl";

import { Input, InputWrapper } from "@/components/ui/input";
import { MENU_SIDEBAR } from "@/config/layout-30.config";

const SEARCH_ITEMS = MENU_SIDEBAR.flatMap(
  (group) => group.children ?? [],
).filter((item): item is typeof item & { path: string; title: string } =>
  Boolean(item.path && item.title),
);

function SearchResult({
  item,
  onSelect,
}: {
  item: { label: string; path: string };
  onSelect: () => void;
}) {
  return (
    <Link
      href={item.path as Route}
      role="option"
      aria-selected="false"
      onClick={onSelect}
      className="block rounded-sm px-2.5 py-2 text-sm text-popover-foreground hover:bg-accent focus-visible:bg-accent"
    >
      {item.label}
    </Link>
  );
}

function SearchResults({
  items,
  onSelect,
  emptyLabel,
}: {
  items: { label: string; path: string }[];
  onSelect: () => void;
  emptyLabel: string;
}) {
  return (
    <div
      id="layout-search-results"
      role="listbox"
      className="absolute end-0 top-[calc(100%+0.5rem)] z-30 w-64 rounded-md border border-border bg-popover p-1 shadow-lg"
    >
      {items.length ? (
        items.map((item) => (
          <SearchResult
            key={`${item.path}:${item.label}`}
            item={item}
            onSelect={onSelect}
          />
        ))
      ) : (
        <p className="px-2.5 py-2 text-sm text-muted-foreground">
          {emptyLabel}
        </p>
      )}
    </div>
  );
}

function useNavigationSearch() {
  const t = useTranslations("layout30");
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(false);
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const items = SEARCH_ITEMS.map((item) => ({
    label: t(item.title as Parameters<typeof t>[0]),
    path: item.path,
  })).filter((item) =>
    item.label.toLocaleLowerCase().includes(normalizedQuery),
  );

  return { active, items, query, setActive, setQuery, t };
}

function SearchControl({
  active,
  label,
  placeholder,
  query,
  setActive,
  setQuery,
}: {
  active: boolean;
  label: string;
  placeholder: string;
  query: string;
  setActive: (active: boolean) => void;
  setQuery: (query: string) => void;
}) {
  return (
    <InputWrapper className="w-full lg:w-55">
      <Search />
      <Input
        type="search"
        value={query}
        aria-label={label}
        aria-controls="layout-search-results"
        aria-expanded={active}
        aria-autocomplete="list"
        placeholder={placeholder}
        onFocus={() => setActive(true)}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") setActive(false);
        }}
      />
    </InputWrapper>
  );
}

export function HeaderSearch() {
  const { active, items, query, setActive, setQuery, t } =
    useNavigationSearch();
  const showResults = active && query.trim().length > 0;

  return (
    <div
      role="search"
      aria-label={t("search.label")}
      className="relative flex items-center gap-2.5"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget))
          setActive(false);
      }}
    >
      <SearchControl
        active={showResults}
        label={t("search.label")}
        placeholder={t("search.placeholder")}
        query={query}
        setActive={setActive}
        setQuery={setQuery}
      />
      {showResults ? (
        <SearchResults
          items={items}
          emptyLabel={t("search.noResults")}
          onSelect={() => {
            setActive(false);
            setQuery("");
          }}
        />
      ) : null}
    </div>
  );
}
